// ============================================================
// diagnostics/log - what happened before it broke.
//
// A crash arrives as "it broke somewhere near the map", and the
// ErrorBoundary's stack says where the render died and nothing about
// the twenty seconds before it: the 429 the heartbeat swallowed, the
// listener that went into backoff, the sign-in that silently expired.
// This is a ring buffer of tagged lines every layer writes to, with
// the stamp a report needs (version, backend, session, role) held
// beside it. Nothing leaves the browser: `report.ts` turns the buffer
// into text and a button copies it.
//
// Pure module: no React, no backend import. `diag` is the app-wide
// instance; tests build their own with `createDiagnostics`.
// ============================================================

type DiagLevel = 'info' | 'warn' | 'error';

export interface DiagEntry {
  /** Epoch millis. */
  t: number;
  level: DiagLevel;
  /** Short area name: 'sync', 'auth', 'presence', 'turns', 'crash'... */
  tag: string;
  msg: string;
  /** Serialisable detail - an `errorSummary`, a status, an id. Never a live object. */
  data?: unknown;
}

/** The stamp printed at the top of a report. Every field optional: set what is known when it is known. */
export interface DiagContext {
  version?: string;
  buildTime?: string;
  backend?: string;
  sessionId?: string | null;
  playerId?: string | null;
  uid?: string | null;
  isGm?: boolean;
  /** House rules that differ from their default, as `key=value`. */
  houseRules?: string[];
}

export interface Diagnostics {
  record(level: DiagLevel, tag: string, msg: string, data?: unknown): void;
  info(tag: string, msg: string, data?: unknown): void;
  warn(tag: string, msg: string, data?: unknown): void;
  error(tag: string, msg: string, data?: unknown): void;
  /** Oldest first. A copy - the buffer is never handed out. */
  entries(): DiagEntry[];
  setContext(patch: DiagContext): void;
  context(): DiagContext;
  clear(): void;
  /** Where each record is also sent from now on - the console in dev. Null stops it. */
  mirrorTo(sink: ((entry: DiagEntry) => void) | null): void;
  /** Told of every record until the returned function is called. Many may listen; the mirror is separate. */
  subscribe(listener: (entry: DiagEntry) => void): () => void;
}

interface DiagnosticsOptions {
  /** Entries kept; the oldest go first. */
  capacity?: number;
  now?: () => number;
  /** Where each record is also sent - the console in dev, nothing in tests. */
  mirror?: (entry: DiagEntry) => void;
}

const DEFAULT_CAPACITY = 500;

/** The newest `capacity` entries, oldest first. */
function createRing(capacity: number) {
  let buffer: DiagEntry[] = [];
  return {
    push(entry: DiagEntry): void {
      buffer.push(entry);
      // Trim by slicing rather than shifting one at a time: a burst of
      // errors past the cap is exactly when this must stay cheap.
      if (buffer.length > capacity) buffer = buffer.slice(buffer.length - capacity);
    },
    all: () => buffer.slice(),
    clear: () => {
      buffer = [];
    },
  };
}

/** Everyone told of every record, each until they unsubscribe. */
function createListeners() {
  const listeners = new Set<(entry: DiagEntry) => void>();
  return {
    tell: (entry: DiagEntry) => listeners.forEach((l) => l(entry)),
    subscribe: (listener: (entry: DiagEntry) => void) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}

export function createDiagnostics(options: DiagnosticsOptions = {}): Diagnostics {
  const ring = createRing(options.capacity ?? DEFAULT_CAPACITY);
  const now = options.now ?? Date.now;
  let mirror = options.mirror ?? null;
  let ctx: DiagContext = {};
  const listeners = createListeners();

  const record = (level: DiagLevel, tag: string, msg: string, data?: unknown) => {
    const entry: DiagEntry = { t: now(), level, tag, msg, ...(data !== undefined ? { data } : {}) };
    ring.push(entry);
    mirror?.(entry);
    listeners.tell(entry);
  };
  return {
    record,
    info: (tag, msg, data) => record('info', tag, msg, data),
    warn: (tag, msg, data) => record('warn', tag, msg, data),
    error: (tag, msg, data) => record('error', tag, msg, data),
    entries: ring.all,
    setContext: (patch) => {
      ctx = { ...ctx, ...patch };
    },
    context: () => ({ ...ctx }),
    clear: ring.clear,
    mirrorTo: (sink) => {
      mirror = sink;
    },
    subscribe: listeners.subscribe,
  };
}

/** An HTTP `status` (PocketBase) and a string `code` (Firestore), each only when present and typed. */
function transportFields(err: object): { status?: number; code?: string } {
  const o = err as { status?: unknown; code?: unknown };
  return {
    ...(typeof o.status === 'number' ? { status: o.status } : {}),
    ...(typeof o.code === 'string' ? { code: o.code } : {}),
  };
}

/**
 * The parts of an error a report can print. Backends disagree on the
 * shape - PocketBase puts an HTTP `status` on it, Firestore a string
 * `code` - so both are kept when present, and a non-Error is stringified.
 */
export function errorSummary(err: unknown): { name?: string; message: string; status?: number; code?: string } {
  if (err instanceof Error) return { name: err.name, message: err.message, ...transportFields(err) };
  if (err && typeof err === 'object') {
    const o = err as { message?: unknown };
    return { message: typeof o.message === 'string' ? o.message : JSON.stringify(err), ...transportFields(err) };
  }
  return { message: String(err) };
}

/**
 * Catch what React cannot: an error thrown outside a render, and a
 * promise nobody awaited. Both went nowhere before this - an async
 * write that threw after its `.catch(() => {})` was removed would have
 * been invisible everywhere but devtools.
 */
export function installGlobalHandlers(target: Window, d: Diagnostics = diag): () => void {
  const onError = (e: Event) => {
    const ev = e as ErrorEvent;
    d.error('uncaught', ev.message || String(ev.error), ev.error ? errorSummary(ev.error) : undefined);
  };
  const onRejection = (e: Event) => {
    const reason = (e as PromiseRejectionEvent).reason;
    const summary = errorSummary(reason);
    d.error('unhandled-rejection', summary.message, summary);
  };
  target.addEventListener('error', onError);
  target.addEventListener('unhandledrejection', onRejection);
  return () => {
    target.removeEventListener('error', onError);
    target.removeEventListener('unhandledrejection', onRejection);
  };
}

/** Keeps the console useful while the buffer does the remembering. `main.jsx` installs it in dev builds only. */
export function consoleMirror(entry: DiagEntry): void {
  const line = `[${entry.tag}] ${entry.msg}`;
  if (entry.level === 'error') console.error(line, entry.data ?? '');
  else if (entry.level === 'warn') console.warn(line, entry.data ?? '');
  else console.info(line, entry.data ?? '');
}

/** The app's one buffer. Silent by default, so tests stay quiet. */
export const diag: Diagnostics = createDiagnostics();

/**
 * A promise nobody awaits - a write fired from a click, a teardown - with
 * its failure kept as one WARNING here instead of an uncaught rejection on
 * the page. Use it in place of `void p` wherever `p` touches the network:
 * on 2026-10-02 a seat offline on PocketBase threw one per such write.
 */
export function warnIfRejected(p: Promise<unknown>, tag: string, what: string): void {
  p.catch((err: unknown) => diag.warn(tag, `${what} failed`, errorSummary(err)));
}
